import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

import { setActiveMenu } from '@/app/store/slices/activeMenuSlice';
import {
    buildInitialState,
    clearServicePointProfile,
    type ServicePointProfile,
    type ServicePointProfileState,
    setServicePointProfile,
} from '@/app/store/slices/servicePointProfileSlice';
import { hydrateSnapshot } from '@/app/store/stateSnapshot/hydrateSnapshotAction';

import { QUOTATION_PAGE_ID, QUOTATION_STEPS, type QuotationStep } from '../constants';
import { isValueProtectionAllowedForProfile } from '../quotation-and-shipping/utils/shipmentValueProtection';
const cachedServicePointData: ServicePointProfileState = buildInitialState();

export type Package = {
    id: string;
    pkgId: string | null;
    packageType: string;
    length: string | null;
    width: string | null;
    height: string | null;
    weight: string | null;
    dimensionUnit: string;
    weightUnit: string;
};

export type LineItem = {
    id: string;
    itemDescription: string;
    validItemDescription?: boolean;
    descriptionQuality?: string;
    hsCode: string;
    quantity: number;
    unit: string;
    weight: string;
    weightUnit: string;
    itemOriginCountry: string;
    itemValue: string;
    itemCurrency: string;
};

export type Shipper = {
    customerId?: string;
    accBilTy?: string;
    accClssTy?: string;
    fullName?: string;
    roleType?: string;
    companyName?: string;
    customerSector?: string;
    isContactVerified?: boolean;
    isManualVerification?: boolean; // MANUAL VERIFICATION SECTION
    isManualVerified?: boolean;
    manualVerificationRemarks?: string;
    email?: string;
    phone?: {
        type?: string;
        phoneIdd?: string | null;
        phoneNumber?: string | null;
    }[];
    address: {
        isCityPostalValidating?: boolean;
        country?: string;
        addressLine1?: string;
        addressLine2?: string;
        addressLine3?: string;
        city?: string;
        postalCode?: string;
        state?: string;
    };
    tax: {
        taxType?: string;
        taxValue?: string;
    }[];
};

export type Receiver = {
    consigneeId?: string;
    fullName?: string;
    roleType?: string;
    companyName?: string;
    customerSector?: string;
    email?: string;
    phone?: {
        type?: string;
        phoneIdd?: string | null;
        phoneNumber?: string | null;
    }[];
    address: {
        isCityPostalValidating?: boolean;
        country?: string;
        addressLine1?: string;
        addressLine2?: string;
        addressLine3?: string;
        city?: string;
        postalCode?: string;
        state?: string;
    };
    tax: {
        taxType?: string;
        taxValue?: string;
    }[];
};

type SanctionCountry = {
    indemnityLtrRqrInd?: string;
    indemnityLtrRqrIndStmt?: string;
    snctnd?: boolean;
};

type CancelDetails = {
    oldShpId: string;
    cnclRsnCd: string;
    srvPntFcId: string;
    hwb?: string;
};

export interface QuotationAndShippingState {
    currentStep: QuotationStep;
    editQuotationId?: string;
    isDuplicating?: boolean;
    /** Set when the flow originates from a "Cancel and Recreate Shipment" action. Consumed by finalize and then cleared. */
    cancelDetails?: CancelDetails | null;
    /** Tracks the page from which an edit/duplicate was initiated (e.g. 'FM_QTN_HST', 'IM_SHP_HST'). */
    editSourcePageId?: string;
    /** Set by the servicePointProfile change reducer when mid-flow reset requires a page redirect. Consumed and cleared by QuotationAndShippingPage. */
    pendingRedirectPageId?: string;
    qtnRefNo?: string;
    qtnId?: string;
    waybillNo?: string;
    wkRef?: string;
    selectedProductCode?: string;
    selectedGlobalProductCode?: string;
    selectedServiceCodes?: string[];
    shpId: string;
    origin: {
        country: string;
        city: string;
        postalCode: string;
        servicePointId: string;
        orgFcCd?: string;
        orgSrvACd?: string;
    };
    destination: {
        country: string;
        city: string;
        postalCode?: string;
        embargoErrorMessage?: string;
        dstSrvACd?: string;
        dstFcCd?: string;
    };
    /** True only when the destination city/postal code combination has passed the validation API.
     *  Reset to false whenever the user edits the city, postal code, or country. */
    isDestinationValidated: boolean;
    isAccountCustomer: boolean;
    accountNumber?: string;
    isAccountNumberValidated?: boolean;
    otpValidationEmail?: string;
    accountTypeCode?: string;
    accountBillTerm?: string[];
    customerDiscount?: string;
    shipmentType: string;
    packages: Package[];
    isDuitable: boolean;
    valueProtection: {
        enabled: boolean;
        currency?: string;
        totalDeclaredValue?: string;
        insuredValue?: string;
        isValueInvalid?: boolean;
    };
    sanctionCountry: SanctionCountry;
    promoCodeName?: string;
    promoCodeAccountNumber?: string;

    //SHIPMENT DETAIL
    shipmentDocumentDescription?: string;
    shipmentDocumentDescriptionText?: string;
    shipmentDocumentOtherDescription?: string;
    shipmentPurpose?: string;
    tyOfExp?: string;
    shipmentDescription?: string;
    shipmentReference?: string;
    shipmentInvoiceType?: string;
    shipmentIncoterm?: string;
    isShipmentInspection?: boolean;
    isShipmentSuspicious?: boolean;

    //finalized shipment data
    finalizedShipment: {
        createdDate?: string;
        summary?: {
            totPcl?: number;
            totWght?: number;
            totWghtUom?: string;
            payMthCd?: string;
            paidAmt?: number;
            paidAmtCurCd?: string;
            totChrgAmt?: number;
            totChrgCurCd?: string;
        };
        pltShp?: 'N' | 'Y';
        adiShp?: 'N' | 'Y';
    };

    lineItems?: LineItem[];

    shipper: Shipper;
    receiver: Receiver;

    skipBackToQuoteStepCheck?: boolean;
    // SNAPSHOT OF SHIPPER AS OF LAST SUCCESSFUL NADB SYNC, TO SKIP RE-VALIDATION WHEN UNTOUCHED
    shipperSyncedSnapshot?: string | null;
}

const initialState: QuotationAndShippingState = {
    currentStep: QUOTATION_STEPS.QUOTE,
    editQuotationId: '',
    isDuplicating: false,
    cancelDetails: null,
    editSourcePageId: undefined,
    pendingRedirectPageId: undefined,
    qtnRefNo: '',
    qtnId: '',
    waybillNo: '',
    wkRef: '',
    selectedProductCode: '',
    selectedServiceCodes: [],
    shpId: '',
    origin: {
        country: cachedServicePointData.profile?.ctryCd || '',
        city: cachedServicePointData.profile?.ctyNm || '',
        postalCode: cachedServicePointData.profile?.zip || '',
        servicePointId: cachedServicePointData.profile?.srvPntFcId || '',
    },
    destination: {
        country: '',
        city: '',
        postalCode: '',
        embargoErrorMessage: '',
    },
    isDestinationValidated: false,
    isAccountCustomer: false,
    isAccountNumberValidated: false,
    accountNumber: '',
    otpValidationEmail: '',
    accountTypeCode: 'PY',
    accountBillTerm: [],
    shipmentType: '',
    packages: [
        {
            id: nanoid(),
            packageType: '',
            pkgId: null,
            length: null,
            width: null,
            height: null,
            weight: null,
            dimensionUnit: 'cm',
            weightUnit: 'kg',
        },
    ],
    isDuitable: false,
    valueProtection: {
        enabled: false,
        currency: '',
        totalDeclaredValue: '0',
        insuredValue: '0',
        isValueInvalid: false,
    },
    sanctionCountry: {
        indemnityLtrRqrInd: '',
        indemnityLtrRqrIndStmt: '',
        snctnd: false,
    },

    //SHIPMENT DETAIL
    shipmentDocumentDescription: '',
    shipmentDocumentDescriptionText: '',
    shipmentDocumentOtherDescription: '',
    shipmentPurpose: '',
    tyOfExp: '',
    shipmentDescription: '',
    shipmentReference: '',
    shipmentInvoiceType: '',
    shipmentIncoterm: '',
    isShipmentInspection: false,
    isShipmentSuspicious: false,
    finalizedShipment: {
        createdDate: '',
        summary: {},
        pltShp: undefined,
        adiShp: undefined,
    },

    // LINE ITEM
    lineItems: [],

    // SHIPPER
    shipper: {
        customerId: '',
        fullName: '',
        roleType: '',
        companyName: '',
        customerSector: '',
        isContactVerified: false,
        isManualVerified: false,
        isManualVerification: false,
        manualVerificationRemarks: '',
        email: '',
        phone: [
            {
                type: '',
                phoneIdd: '',
                phoneNumber: '',
            },
        ],
        address: {
            country: '',
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            city: '',
            postalCode: '',
            state: '',
        },
        tax: [],
    },
    receiver: {
        consigneeId: '',
        fullName: '',
        roleType: '',
        companyName: '',
        customerSector: '',
        email: '',
        phone: [
            {
                type: '',
                phoneIdd: '',
                phoneNumber: '',
            },
        ],
        address: {
            country: '',
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            city: '',
            postalCode: '',
            state: '',
        },
        tax: [],
    },
    promoCodeName: '',
    promoCodeAccountNumber: '',
    shipperSyncedSnapshot: null,
};

const createReceiverForDestination = (country: string): Receiver => ({
    ...initialState.receiver,
    phone: (initialState.receiver.phone ?? []).map((phone) => ({ ...phone })),
    address: {
        ...initialState.receiver.address,
        country,
    },
    tax: (initialState.receiver.tax ?? []).map((tax) => ({ ...tax })),
});

const shouldResetReceiverForDestination = (
    currentDestinationCountry: string,
    receiverCountry: string,
    nextDestinationCountry: string,
) => {
    const normalizedNextCountry = nextDestinationCountry.trim().toUpperCase();
    return (
        currentDestinationCountry.trim().toUpperCase() !== normalizedNextCountry ||
        receiverCountry.trim().toUpperCase() !== normalizedNextCountry
    );
};

const quotationAndShippingSlice = createSlice({
    name: 'quotationAndShipping',
    initialState: initialState,
    reducers: {
        updateCurrentStep(state, action: PayloadAction<QuotationAndShippingState['currentStep']>) {
            // Reset promo code when navigating back to the QUOTE step from a later step
            // (e.g. from Review Quotation), so a stale applied promo isn't shown on re-quote.
            if (
                action.payload === QUOTATION_STEPS.QUOTE &&
                state.currentStep !== QUOTATION_STEPS.QUOTE
            ) {
                state.promoCodeName = '';
                state.promoCodeAccountNumber = '';
            }
            state.currentStep = action.payload;
        },
        updateQtnRefNo(state, action: PayloadAction<QuotationAndShippingState['qtnRefNo']>) {
            state.qtnRefNo = action.payload;
        },
        updateQtnId(state, action: PayloadAction<QuotationAndShippingState['qtnId']>) {
            state.qtnId = action.payload;
        },

        updateEditQuotationId(
            state,
            action: PayloadAction<QuotationAndShippingState['editQuotationId']>,
        ) {
            state.editQuotationId = action.payload;
        },
        updateIsDuplicating(state, action: PayloadAction<boolean>) {
            state.isDuplicating = action.payload;
        },
        updateCancelDetails(state, action: PayloadAction<CancelDetails | null>) {
            state.cancelDetails = action.payload;
        },
        updateEditSourcePageId(
            state,
            action: PayloadAction<QuotationAndShippingState['editSourcePageId']>,
        ) {
            state.editSourcePageId = action.payload;
        },
        clearPendingRedirect(state) {
            state.pendingRedirectPageId = undefined;
        },
        updateWaybillNo(state, action: PayloadAction<QuotationAndShippingState['waybillNo']>) {
            state.waybillNo = action.payload;
        },
        updateShpId(state, action: PayloadAction<QuotationAndShippingState['shpId']>) {
            state.shpId = action.payload;
        },
        updateOrigin(state, action: PayloadAction<QuotationAndShippingState['origin']>) {
            state.origin = action.payload;
        },
        updateDestination(
            state,
            action: PayloadAction<Partial<QuotationAndShippingState['destination']>>,
        ) {
            const shouldResetReceiver =
                action.payload.country !== undefined &&
                shouldResetReceiverForDestination(
                    state.destination.country,
                    state.receiver.address.country,
                    action.payload.country,
                );
            state.destination = {
                ...state.destination,
                ...action.payload,
            };
            if (shouldResetReceiver) {
                state.receiver = createReceiverForDestination(state.destination.country);
            } else if (action.payload.country !== undefined) {
                state.receiver.address.country = state.destination.country;
            }
        },
        updateIsDestinationValidated(state, action: PayloadAction<boolean>) {
            state.isDestinationValidated = action.payload;
        },
        updateIsAccountCustomer(state, action: PayloadAction<boolean>) {
            state.isAccountCustomer = action.payload;
        },
        updateIsAccountNumberValidated(state, action: PayloadAction<boolean>) {
            state.isAccountNumberValidated = action.payload;
        },
        updateAccountNumber(state, action: PayloadAction<string | undefined>) {
            state.accountNumber = action.payload;
        },
        updateOtpValidationEmail(state, action: PayloadAction<string>) {
            state.otpValidationEmail = action.payload;
        },
        updateAccountTypeCode(state, action: PayloadAction<string | undefined>) {
            state.accountTypeCode = action.payload;
        },
        updateAccountBillTerm(state, action: PayloadAction<string[] | undefined>) {
            state.accountBillTerm = action.payload;
        },
        updateCustomerDiscount(state, action: PayloadAction<string | undefined>) {
            state.customerDiscount = action.payload;
        },
        updateShipmentType(
            state,
            action: PayloadAction<QuotationAndShippingState['shipmentType']>,
        ) {
            state.shipmentType = action.payload;
        },
        addPackage(state, action: PayloadAction<Omit<Package, 'id'>>) {
            const newPackage: Package = {
                id: nanoid(),
                ...action.payload,
            };
            state.packages.push(newPackage);
        },
        updatePackage(
            state,
            action: PayloadAction<{
                id: string;
                changes: Partial<Omit<Package, 'id'>>;
            }>,
        ) {
            const { id, changes } = action.payload;
            const index = state.packages.findIndex((pkg) => pkg.id === id);

            if (index !== -1) {
                state.packages[index] = {
                    ...state.packages[index],
                    ...changes,
                };
            }
        },

        deletePackage: (state, action: PayloadAction<string>) => {
            if (state.packages.length <= 1) {
                return;
            }
            const targetId = action.payload;
            state.packages = state.packages.filter((pkg) => pkg.id !== targetId);
        },

        resetPackages: (state) => {
            state.packages = [
                {
                    ...initialState.packages[0],
                    id: nanoid(),
                },
            ];
        },

        updateIsDutiable(state, action: PayloadAction<boolean>) {
            state.isDuitable = action.payload;
        },

        updateValueProtection(
            state,
            action: PayloadAction<Partial<QuotationAndShippingState['valueProtection']>>,
        ) {
            state.valueProtection = {
                ...(state.valueProtection || {}),
                ...action.payload,
            };
        },

        updateSanctionCountry(
            state,
            action: PayloadAction<Partial<QuotationAndShippingState['sanctionCountry']>>,
        ) {
            state.sanctionCountry = {
                ...(state.sanctionCountry || {}),
                ...action.payload,
            };
        },

        resetSanctionCountry(state) {
            state.sanctionCountry = {
                indemnityLtrRqrInd: '',
                indemnityLtrRqrIndStmt: '',
                snctnd: false,
            };
        },

        //SHIPMENT DETAIL
        updateShipmentDocumentDescription(state, action: PayloadAction<string>) {
            state.shipmentDocumentDescription = action.payload;
        },

        updateShipmentDocumentDescriptionText(state, action: PayloadAction<string>) {
            state.shipmentDocumentDescriptionText = action.payload;
        },

        updateShipmentDocumentOtherDescription(state, action: PayloadAction<string>) {
            state.shipmentDocumentOtherDescription = action.payload;
        },

        updateShipmentPurpose(state, action: PayloadAction<string>) {
            state.shipmentPurpose = action.payload;
        },
        updateTyOfExp(state, action: PayloadAction<string>) {
            state.tyOfExp = action.payload;
        },
        updateShipmentDescription(state, action: PayloadAction<string>) {
            state.shipmentDescription = action.payload;
        },

        updateShipmentReference(state, action: PayloadAction<string>) {
            state.shipmentReference = action.payload;
        },

        updateShipmentInvoiceType(state, action: PayloadAction<string>) {
            state.shipmentInvoiceType = action.payload;
        },
        updateShipmentIncoterm(state, action: PayloadAction<string>) {
            state.shipmentIncoterm = action.payload;
        },

        updateIsShipmentInspection(state, action: PayloadAction<boolean>) {
            state.isShipmentInspection = action.payload;
        },

        updateIsShipmentSuspicious(state, action: PayloadAction<boolean>) {
            state.isShipmentSuspicious = action.payload;
        },

        updatePromoCodeName(state, action: PayloadAction<string>) {
            state.promoCodeName = action.payload;
        },

        updatePromoCodeAccountNumber(state, action: PayloadAction<string>) {
            state.promoCodeAccountNumber = action.payload;
        },

        // Sets both promo code fields atomically in a single dispatch so
        // dependent selectors/memos (e.g. quotationPayload) only recompute
        // once, avoiding duplicate/racing getQuotation calls.
        updatePromoCode(
            state,
            action: PayloadAction<{ promoCodeName: string; promoCodeAccountNumber: string }>,
        ) {
            state.promoCodeName = action.payload.promoCodeName;
            state.promoCodeAccountNumber = action.payload.promoCodeAccountNumber;
        },

        addLineItems(state, action: PayloadAction<LineItem[]>) {
            if (!state.lineItems) state.lineItems = [];
            state.lineItems.push(...action.payload);
        },

        updateLineItem(state, action: PayloadAction<{ index: number; item: LineItem }>) {
            if (state.lineItems && state.lineItems[action.payload.index]) {
                state.lineItems[action.payload.index] = action.payload.item;
            }
        },

        deleteLineItem(state, action: PayloadAction<number>) {
            if (state.lineItems) {
                state.lineItems.splice(action.payload, 1);
            }
        },

        resetLineItems(state) {
            state.lineItems = [];
        },

        /** Applies a new currency to every existing line item atomically. */
        updateAllLineItemsCurrency(state, action: PayloadAction<string>) {
            if (state.lineItems) {
                state.lineItems = state.lineItems.map((item) => ({
                    ...item,
                    itemCurrency: action.payload,
                }));
            }
        },

        updateSkipBackToQuoteStepCheck(state, action: PayloadAction<boolean>) {
            state.skipBackToQuoteStepCheck = action.payload;
        },

        updateShipper(state, action: PayloadAction<Partial<QuotationAndShippingState['shipper']>>) {
            state.shipper = {
                ...state.shipper,
                ...action.payload,
            };
        },

        updateShipperAddress(
            state,
            action: PayloadAction<Partial<QuotationAndShippingState['shipper']['address']>>,
        ) {
            state.shipper.address = {
                ...state.shipper.address,
                ...action.payload,
            };
        },

        resetShipperDetails(state) {
            // RESET SHIPPER DETAILS BUT KEEP THE COUNTRY AS IT IS
            const currentCountry = state.shipper.address?.country ?? '';

            state.shipper = {
                ...initialState.shipper,
                phone: (initialState.shipper.phone ?? []).map((p) => ({ ...p })),
                address: {
                    ...initialState.shipper.address,
                    country: currentCountry, // keep existing country
                },
                tax: (initialState.shipper.tax ?? []).map((t) => ({ ...t })),
                roleType: initialState.shipper.roleType,
            };
        },

        updateReceiver(
            state,
            action: PayloadAction<Partial<QuotationAndShippingState['receiver']>>,
        ) {
            state.receiver = {
                ...state.receiver,
                ...action.payload,
            };
        },

        updateReceiverAddress(
            state,
            action: PayloadAction<Partial<QuotationAndShippingState['receiver']['address']>>,
        ) {
            state.receiver.address = {
                ...state.receiver.address,
                ...action.payload,
            };
        },

        resetReceiverDetails(state) {
            state.receiver = createReceiverForDestination(state.destination.country);
        },

        resetQuotationAndShippingDetails: (state) => {
            // Preserve only the selected service-point origin. Clearing the edit
            // context prevents a remounted Quote & Ship page from refetching the
            // previous quotation by editQuotationId.
            const currentOrigin = state.origin;

            Object.assign(state, initialState);
            state.origin = currentOrigin;

            state.packages = [
                {
                    ...initialState.packages[0],
                    id: nanoid(),
                },
            ];
        },

        loadQuotationForEdit(state, action: PayloadAction<Partial<QuotationAndShippingState>>) {
            const payload = action.payload;
            if (payload.qtnRefNo !== undefined) state.qtnRefNo = payload.qtnRefNo;
            if (payload.selectedProductCode !== undefined)
                state.selectedProductCode = payload.selectedProductCode;
            if (payload.selectedGlobalProductCode !== undefined)
                state.selectedGlobalProductCode = payload.selectedGlobalProductCode;
            if (payload.selectedServiceCodes !== undefined)
                state.selectedServiceCodes = payload.selectedServiceCodes;
            if (payload.shipmentType !== undefined) state.shipmentType = payload.shipmentType;
            if (payload.isDuitable !== undefined) state.isDuitable = payload.isDuitable;
            if (payload.origin) {
                state.origin = { ...state.origin, ...payload.origin };
            }
            if (payload.isAccountCustomer !== undefined)
                state.isAccountCustomer = payload.isAccountCustomer;
            if (payload.accountNumber !== undefined) state.accountNumber = payload.accountNumber;

            if (payload.isAccountNumberValidated !== undefined)
                state.isAccountNumberValidated = payload.isAccountNumberValidated;

            if (payload.accountTypeCode !== undefined)
                state.accountTypeCode = payload.accountTypeCode;
            if (payload.customerDiscount !== undefined)
                state.customerDiscount = payload.customerDiscount;
            if (payload.destination) {
                const shouldResetReceiver =
                    payload.destination.country !== undefined &&
                    shouldResetReceiverForDestination(
                        state.destination.country,
                        state.receiver.address.country,
                        payload.destination.country,
                    );
                state.destination = { ...state.destination, ...payload.destination };
                if (shouldResetReceiver) {
                    state.receiver = createReceiverForDestination(state.destination.country);
                } else if (payload.destination.country !== undefined) {
                    state.receiver.address.country = state.destination.country;
                }
                // A destination loaded from a saved quotation was already validated at creation time.
                if (payload.destination.city) state.isDestinationValidated = true;
            }
            if (payload.valueProtection) {
                state.valueProtection = { ...state.valueProtection, ...payload.valueProtection };
            }
            if (payload.packages) {
                state.packages = payload.packages;
            }
            if (payload.promoCodeName) {
                state.promoCodeName = payload.promoCodeName;
            }
            state.tyOfExp = payload.tyOfExp ?? '';
        },

        loadQuotationForPartialShipment(
            state,
            action: PayloadAction<Partial<QuotationAndShippingState>>,
        ) {
            const payload = action.payload;
            if (payload.shpId !== undefined) state.shpId = payload.shpId;
            if (payload.qtnId !== undefined) state.qtnId = payload.qtnId;
            if (payload.qtnRefNo !== undefined) state.qtnRefNo = payload.qtnRefNo;
            if (payload.waybillNo !== undefined) state.waybillNo = payload.waybillNo;
            if (payload.wkRef !== undefined) state.wkRef = payload.wkRef;
            if (payload.selectedProductCode !== undefined)
                state.selectedProductCode = payload.selectedProductCode;
            if (payload.selectedGlobalProductCode !== undefined)
                state.selectedGlobalProductCode = payload.selectedGlobalProductCode;
            if (payload.selectedServiceCodes !== undefined)
                state.selectedServiceCodes = payload.selectedServiceCodes;
            if (payload.shipmentType !== undefined) state.shipmentType = payload.shipmentType;
            if (payload.isDuitable !== undefined) state.isDuitable = payload.isDuitable;
            if (payload.accountNumber !== undefined) {
                state.accountNumber = payload.accountNumber;
                state.isAccountCustomer = Boolean(payload.accountNumber);
            }
            if (payload.isAccountNumberValidated !== undefined)
                state.isAccountNumberValidated = payload.isAccountNumberValidated;
            if (payload.accountTypeCode !== undefined)
                state.accountTypeCode = payload.accountTypeCode;
            if (payload.isAccountNumberValidated !== undefined)
                state.isAccountNumberValidated = payload.isAccountNumberValidated;
            if (payload.shipmentDescription !== undefined)
                state.shipmentDescription = payload.shipmentDescription;
            /* //this got issue, saving as draft doesn't save other's description
            if (payload.shipmentDocumentOtherDescription !== undefined)
                state.shipmentDocumentOtherDescription = payload.shipmentDocumentOtherDescription;
            */
            if (payload.shipmentReference !== undefined)
                state.shipmentReference = payload.shipmentReference;
            if (payload.shipmentDocumentDescription !== undefined)
                state.shipmentDocumentDescription = payload.shipmentDocumentDescription;
            if (payload.shipmentDocumentDescriptionText !== undefined)
                state.shipmentDocumentDescriptionText = payload.shipmentDocumentDescriptionText;
            if (payload.shipmentPurpose !== undefined)
                state.shipmentPurpose = payload.shipmentPurpose;
            state.tyOfExp = payload.tyOfExp ?? '';
            if (payload.shipmentInvoiceType !== undefined)
                state.shipmentInvoiceType = payload.shipmentInvoiceType;
            if (payload.shipmentIncoterm !== undefined)
                state.shipmentIncoterm = payload.shipmentIncoterm;
            if (payload.isShipmentInspection !== undefined)
                state.isShipmentInspection = payload.isShipmentInspection;
            if (payload.isShipmentSuspicious !== undefined)
                state.isShipmentSuspicious = payload.isShipmentSuspicious;
            if (payload.origin) {
                state.origin = { ...state.origin, ...payload.origin };
            }
            if (payload.destination) {
                state.destination = { ...state.destination, ...payload.destination };
                // A destination loaded from a saved shipment was already validated at creation time.
                if (payload.destination.city) state.isDestinationValidated = true;
            }
            if (payload.valueProtection) {
                state.valueProtection = { ...state.valueProtection, ...payload.valueProtection };
            }
            if (payload.packages) {
                state.packages = payload.packages;
            }
            if (payload.shipper) {
                state.shipper = { ...state.shipper, ...payload.shipper };
            }
            if (payload.receiver) {
                state.receiver = { ...state.receiver, ...payload.receiver };
            }
            if (payload.lineItems) {
                state.lineItems = payload.lineItems;
            }
            if (payload.promoCodeAccountNumber && payload.promoCodeName) {
                state.promoCodeName = payload.promoCodeName;
                state.promoCodeAccountNumber = payload.promoCodeAccountNumber;
            }
        },

        updateFinalizedShipment(
            state,
            action: PayloadAction<QuotationAndShippingState['finalizedShipment']>,
        ) {
            state.finalizedShipment = {
                ...state.finalizedShipment,
                ...action.payload,
            };
        },

        updateShipperSyncedSnapshot(state, action: PayloadAction<string | null>) {
            state.shipperSyncedSnapshot = action.payload;
        },
    },

    // listening to servicePointProfileSlice actions to update origin details
    extraReducers: (builder) => {
        builder.addCase(
            setServicePointProfile,
            (state, action: PayloadAction<ServicePointProfile>) => {
                const profile = action.payload;
                if (!profile) return;

                const newOrigin = {
                    country: profile.ctryCd || '',
                    city: profile.ctyNm || '',
                    postalCode: profile.zip || '',
                    servicePointId: profile.srvPntFcId || '',
                };

                // Capture the source page before any reset so we can redirect back.
                const sourcePageId = state.editSourcePageId;

                // If the user was mid-flow (past the initial QUOTE step), reset the
                // entire slice back to initialState so the page returns to step 1
                // (Quote & Ship) with the new service point context pre-filled as origin.
                // If still on QUOTE step, just update origin — no in-flight work is lost.
                if (state.currentStep !== QUOTATION_STEPS.QUOTE) {
                    Object.assign(state, initialState);
                    state.packages = [{ ...initialState.packages[0], id: nanoid() }];
                }

                // Signal QuotationAndShippingPage to navigate back to the source page
                // whenever editSourcePageId is set — regardless of which step the user
                // was on (covers both mid-flow edits and edits still on the QUOTE step).
                if (sourcePageId) {
                    state.pendingRedirectPageId = sourcePageId;
                }

                state.origin = newOrigin;

                // Clear stale value protection selections if the new profile no longer
                // allows it (e.g. admin disabled insEnbld after the user had already
                // enabled/filled it) so hidden fields never block "Get Quote" or leak
                // into the payload. No-op when the branch above already reset the slice.
                if (state.valueProtection.enabled && !isValueProtectionAllowedForProfile(profile)) {
                    state.valueProtection = { ...initialState.valueProtection };
                }
            },
        );

        builder.addCase(clearServicePointProfile, (state) => {
            state.origin = {
                country: '',
                city: '',
                postalCode: '',
                servicePointId: '',
            };
        });

        // On page refresh the encrypted localStorage snapshot is restored and
        // servicePointProfileSlice re-hydrates its own state, but this slice has
        // no matching handler — so origin.country/city stay '' and isRouteValid
        // keeps "Get Quote" disabled until the user manually re-selects the SP.
        // Mirror the setServicePointProfile path here so origin is populated the
        // moment the snapshot is dispatched, before any component renders.
        builder.addCase(hydrateSnapshot, (state, action) => {
            const profile = action.payload.servicePointProfile?.profile;
            if (!profile) return;
            state.origin = {
                country: (profile as ServicePointProfile).ctryCd || '',
                city: (profile as ServicePointProfile).ctyNm || '',
                postalCode: (profile as ServicePointProfile).zip || '',
                servicePointId: profile.srvPntFcId || '',
            };
        });

        // case to check if the user is leaving the quotation page, if yes then reset the state except for the origin
        builder.addCase(setActiveMenu, (state, action: PayloadAction<string>) => {
            if (
                action.payload !== QUOTATION_PAGE_ID &&
                (state.qtnRefNo !== '' ||
                    state.editQuotationId !== '' ||
                    state.destination.country !== '')
            ) {
                const currentOrigin = state.origin;
                Object.assign(state, initialState);
                state.origin = currentOrigin;

                state.packages = [
                    {
                        ...initialState.packages[0],
                        id: nanoid(),
                    },
                ];
            }
        });
    },
});

export const {
    updateCurrentStep,
    updateQtnRefNo,
    updateQtnId,
    updateEditQuotationId,
    updateIsDuplicating,
    updateCancelDetails,
    updateEditSourcePageId,
    clearPendingRedirect,
    loadQuotationForEdit,
    loadQuotationForPartialShipment,
    updateWaybillNo,
    updateShpId,
    updateOrigin,
    updateDestination,
    updateIsDestinationValidated,
    updateIsAccountCustomer,
    updateIsAccountNumberValidated,
    updateAccountNumber,
    updateOtpValidationEmail,
    updateAccountTypeCode,
    updateAccountBillTerm,
    updateCustomerDiscount,
    updateShipmentType,
    addPackage,
    updatePackage,
    deletePackage,
    resetPackages,
    updateIsDutiable,
    updateValueProtection,
    updateSanctionCountry,
    resetSanctionCountry,
    updatePromoCodeName,
    updatePromoCodeAccountNumber,
    updatePromoCode,

    updateShipmentDocumentDescription,
    updateShipmentDocumentDescriptionText,
    updateShipmentDocumentOtherDescription,
    updateShipmentPurpose,
    updateTyOfExp,
    updateShipmentDescription,
    updateShipmentReference,
    updateShipmentInvoiceType,
    updateShipmentIncoterm,
    updateIsShipmentInspection,
    updateIsShipmentSuspicious,
    updateFinalizedShipment,

    addLineItems,
    updateLineItem,
    deleteLineItem,
    resetLineItems,
    updateAllLineItemsCurrency,
    updateShipper,
    updateShipperAddress,
    updateShipperSyncedSnapshot,
    resetReceiverDetails,
    updateReceiver,
    updateReceiverAddress,

    resetQuotationAndShippingDetails,
    resetShipperDetails,

    updateSkipBackToQuoteStepCheck,
} = quotationAndShippingSlice.actions;
export default quotationAndShippingSlice.reducer;
